import { useEffect, useMemo, useState } from 'react';

import { PrimeirosPassos } from '@/components/Onboarding/PrimeirosPassos';
import { Link } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';

import { Button } from '@/components/atoms';
import { formatNewsDate } from '@/utils/datetime';
import { volumeDeBusca } from '@/utils/trends';
import { FrammingModal } from '@/components/FrammingModal/FrammingModal';
import { ApiError } from '@/services/api';
import { boardApi } from '@/services/board';
import { destinationsApi } from '@/services/config';
import { usePlatformAccess } from '@/services/platform';
import { useMarcaAssumida } from '@/store/useMarcaAssumida';
import { useNavPulse } from '@/store/useNavPulse';
import {
  intelligenceService,
  type NewsCard,
  type TrendItem,
} from '@/services/intelligence';

import styles from './Intelligence.module.css';

// Só o que o backend realmente produz: o mapa de categorias manda politics e
// law and government para "Mundo" (intelligence_scoring.py:16-19), então filtrar
// por "Política" devolvia sempre zero.
const EDITORIAS = ['Esportes', 'Economia', 'Mundo', 'Tecnologia', 'Tempo', 'Entretenimento', 'Saúde'];
const HOURS = [
  { v: 4, label: '4h' },
  { v: 24, label: '24h' },
  { v: 48, label: '48h' },
  { v: 168, label: '7 dias' },
];
// Geografia: Brasil (país) + os 27 estados via códigos ISO 3166-2.
const GEOS = [
  { v: 'BR', label: 'Brasil (país)' },
  { v: 'BR-AC', label: 'Acre' },
  { v: 'BR-AL', label: 'Alagoas' },
  { v: 'BR-AP', label: 'Amapá' },
  { v: 'BR-AM', label: 'Amazonas' },
  { v: 'BR-BA', label: 'Bahia' },
  { v: 'BR-CE', label: 'Ceará' },
  { v: 'BR-DF', label: 'Distrito Federal' },
  { v: 'BR-ES', label: 'Espírito Santo' },
  { v: 'BR-GO', label: 'Goiás' },
  { v: 'BR-MA', label: 'Maranhão' },
  { v: 'BR-MT', label: 'Mato Grosso' },
  { v: 'BR-MS', label: 'Mato Grosso do Sul' },
  { v: 'BR-MG', label: 'Minas Gerais' },
  { v: 'BR-PA', label: 'Pará' },
  { v: 'BR-PB', label: 'Paraíba' },
  { v: 'BR-PR', label: 'Paraná' },
  { v: 'BR-PE', label: 'Pernambuco' },
  { v: 'BR-PI', label: 'Piauí' },
  { v: 'BR-RJ', label: 'Rio de Janeiro' },
  { v: 'BR-RN', label: 'Rio Grande do Norte' },
  { v: 'BR-RS', label: 'Rio Grande do Sul' },
  { v: 'BR-RO', label: 'Rondônia' },
  { v: 'BR-RR', label: 'Roraima' },
  { v: 'BR-SC', label: 'Santa Catarina' },
  { v: 'BR-SP', label: 'São Paulo' },
  { v: 'BR-SE', label: 'Sergipe' },
  { v: 'BR-TO', label: 'Tocantins' },
];

const geoLabel = (v: string) => GEOS.find((g) => g.v === v)?.label ?? v;

function timeAgo(ts: number | null): string | null {
  if (!ts) return null;
  const diff = Date.now() / 1000 - ts;
  if (diff < 0) return 'agora';
  const h = Math.floor(diff / 3600);
  if (h < 1) return `há ${Math.max(1, Math.floor(diff / 60))} min`;
  if (h < 24) return `há ${h}h`;
  return `há ${Math.floor(h / 24)} d`;
}

function falhaDasTendencias(err: unknown): string {
  // A CAUSA vem do backend (409 cota · 503 sem chave · 502 provedor), que desde
  // 07/08 escreve a frase certa em cada caso. A tela repete — não adivinha.
  // O texto antigo culpava a chave SerpApi para QUALQUER falha, inclusive teto
  // do plano com a credencial certa: mandava o operador consertar o que não
  // estava quebrado.
  if (err instanceof ApiError) {
    const detalhe = (err.body as { detail?: unknown })?.detail;
    if (typeof detalhe === 'string' && detalhe) return detalhe;
  }
  return 'Falha ao carregar tendências. Tente atualizar; se persistir, veja os alertas.';
}

// O POST /board/from-intelligence responde 422 quando a sessao e da casa e
// nenhuma marca foi assumida — e 422 e o unico status que o cliente HTTP NAO
// transforma em alarme global (reportarFalha em services/api.ts), porque e
// orientacao da tela e nao incidente. Sem esta leitura o clique em "Produzir"
// nao fazia absolutamente nada (F-484). `detail` chega como string OU como
// objeto {erro, motivo}: as duas formas saem deste mesmo endpoint.
function motivoDoEnvio(err: unknown): string {
  if (err instanceof ApiError) {
    const detalhe = (err.body as { detail?: unknown } | null)?.detail;
    if (typeof detalhe === 'string' && detalhe) return detalhe;
    if (detalhe && typeof detalhe === 'object') {
      const { erro, motivo } = detalhe as { erro?: string; motivo?: string };
      // Comparar a frase inteira do backend quebraria na primeira vez que
      // alguem a reescrevesse; "marca" e o que identifica este caso.
      if (err.status === 422 && erro?.includes('marca')) {
        return 'Escolha a marca no topo (sessão da casa) antes de produzir.';
      }
      if (erro) return motivo ? `${erro} — ${motivo}` : erro;
      if (motivo) return motivo;
    }
  }
  return 'Falha ao enviar para o quadro. Tente novamente.';
}

function bandLabel(band: string): string {
  if (band === 'alta') return 'Ótimo tema';
  if (band === 'media') return 'Tema viável';
  return 'Tema fraco';
}

function flagLabel(flag: string): string {
  if (flag === 'generico') return 'termo genérico';
  if (flag === 'evergreen') return 'recorrente';
  if (flag === 'navegacional') return 'navegacional';
  return flag;
}

function scoreColor(score: number): string {
  if (score >= 70) return 'var(--ok)';
  if (score >= 45) return 'var(--warn)';
  return 'var(--text-4)';
}

function verdictTone(verdict: string): string {
  switch (verdict) {
    case 'FURO':
      return styles.vFuro;
    case 'COBRIR':
      return styles.vCobrir;
    case 'SATURADO':
      return styles.vSaturado;
    default:
      return styles.vIgnorar;
  }
}

export function Intelligence() {
  const [geo, setGeo] = useState('BR');
  // Janela padrão: 24h. Nasceu 4h (02/07, "o que está quente AGORA"), mas o
  // Google devolve POUCAS tendências em 4h — medido em 22/09/2026 às 12h50 BRT:
  // 7 na janela de 4h contra 75 na de 24h, e as 7 já tinham sido produzidas
  // pela mesa e pela esteira. A tela ficava com meia dúzia de cards, todos
  // "publicado", e parecia que uma régua escondia o resto. Não há régua:
  // o backend não corta por score e a tela não filtra. A alavanca é a janela
  // que se pergunta ao Google (mesma lição de 06/08). O produtor ainda pode
  // fechar para 4h; o card já diz "em alta"/"Encerrada" para cada tendência.
  const [hours, setHours] = useState(24);
  const [category, setCategory] = useState('');
  const [onlyActive, setOnlyActive] = useState(false); // nasce desligado: mostra tudo por padrão
  const [selected, setSelected] = useState<TrendItem | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);

  // A sessao da casa (staff) nao tem client_id: sem uma marca explicita o
  // backend recusa o card com 422 e ele nao apareceria no quadro de cliente
  // nenhum. Para TENANT o valor fica undefined de proposito — la a marca vem do
  // client_id da sessao, e mandar um slug daqui so criaria um jeito novo de
  // errar (o slug persistido pode ter sobrado de outra sessao no navegador).
  const { isStaff } = usePlatformAccess();
  const marcaAssumida = useMarcaAssumida((s) => s.slug);
  const marca = isStaff ? marcaAssumida ?? undefined : undefined;

  const trendsQuery = useQuery({
    queryKey: ['intel', 'trends', geo, hours, category, onlyActive],
    queryFn: () => intelligenceService.getTrends({ geo, hours, category, onlyActive }),
  });

  const items = trendsQuery.data?.items ?? [];
  // 200 com items:[] tem DUAS causas opostas: janela sem tendência, ou busca
  // que nem aconteceu (sem chave SERP). O backend agora diz qual é; a tela
  // repete a frase dele em vez de mostrar o vazio como se fosse resultado.
  const aviso = trendsQuery.data?.aviso ?? null;

  // M3: "este ASSUNTO já está publicado?" respondido pelo REGISTRO (pautas
  // publicadas, similaridade calibrada) — não por comparação de string com a
  // coluna do quadro, que ficava cega quando a esteira publicava com título
  // diferente da query. Devolve também a ORIGEM (esteira/mesa) e o quando.
  const temas = useMemo(() => items.map((t) => t.query), [items]);
  const estadoQuery = useQuery({
    queryKey: ['estado-conteudo', temas],
    queryFn: () => boardApi.estadoConteudo(temas),
    enabled: temas.length > 0,
    staleTime: 60_000,
  });
  const estados = estadoQuery.data ?? {};

  return (
    <section className={styles.root}>
      {/* Primeira tela do produto depois do login. Antes de qualquer coisa,
          quem acabou de assinar precisa saber o que fazer — o painel some
          sozinho quando os quatro passos estiverem feitos. */}
      <PrimeirosPassos />
      <header className={styles.head}>
        <div>
          <h1 className={styles.title}>Inteligência · Tendências</h1>
          <p className={styles.subtitle}>
            O que o Brasil procura agora, ranqueado por relevância editorial.
            Cada card já traz tudo da tendência — abre para a cobertura e as ações.
          </p>
        </div>
      </header>

      {feedback && (
        <p className={styles.flash}>
          <span onClick={() => setFeedback(null)} style={{ cursor: 'pointer' }}>{feedback}</span>
          {feedback.includes('quadro') && (
            <>
              {' '}
              <Link to="/producao" style={{ marginLeft: 8, fontWeight: 500 }}>
                ver no quadro →
              </Link>
            </>
          )}
        </p>
      )}

      {/* ─── Filtros ─── */}
      <div className={styles.filters}>
        <label className={styles.filter}>
          <span>Geografia</span>
          <select value={geo} onChange={(e) => setGeo(e.target.value)}>
            {GEOS.map((g) => (
              <option key={g.v} value={g.v}>
                {g.label}
              </option>
            ))}
          </select>
        </label>
        <label className={styles.filter}>
          <span>Janela</span>
          <select value={hours} onChange={(e) => setHours(Number(e.target.value))}>
            {HOURS.map((h) => (
              <option key={h.v} value={h.v}>
                {h.label}
              </option>
            ))}
          </select>
        </label>
        <label className={styles.filter}>
          <span>Editoria</span>
          <select value={category} onChange={(e) => setCategory(e.target.value)}>
            <option value="">Todas</option>
            {EDITORIAS.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </label>
        <label className={styles.toggle}>
          <input
            type="checkbox"
            checked={onlyActive}
            onChange={(e) => setOnlyActive(e.target.checked)}
          />
          <span>só ativas</span>
        </label>
        <div className={styles.filterSpacer} />
        <span className={styles.count}>
          {trendsQuery.isFetching ? 'carregando…' : `${items.length} tendências`}
        </span>
        <Button size="sm" variant="secondary" onClick={() => trendsQuery.refetch()}>
          Atualizar
        </Button>
      </div>

      {trendsQuery.isError && (
        <p className={styles.error}>{falhaDasTendencias(trendsQuery.error)}</p>
      )}

      {aviso && !trendsQuery.isError && <p className={styles.error}>{aviso}</p>}

      {/* ─── Grelha de cards ─── */}
      <div className={styles.cards}>
        {items.map((t) => (
          <TrendCard
            key={t.query}
            t={t}
            geo={geo}
            estado={estados[t.query]}
            marca={marca}
            onOpen={() => { setSelected(t); setFeedback(null); }}
            onProduced={(msg) => setFeedback(msg)}
          />
        ))}
      </div>
      {!trendsQuery.isLoading && items.length === 0 && !trendsQuery.isError && !aviso && (
        <p className={styles.empty}>Nenhuma tendência com estes filtros.</p>
      )}

      {/* ─── Modal denso ─── */}
      {selected && (
        <TopicModal
          item={selected}
          marca={marca}
          onClose={() => setSelected(null)}
          onFeedback={(m) => { setFeedback(m); setSelected(null); }}
        />
      )}
    </section>
  );
}

// ─── Card de tendência ───────────────────────────────────────────────────────

// Origem da publicação, na língua da redação (fonte técnica → rótulo humano).
const ORIGEM_LABEL: Record<string, string> = {
  quentes: 'esteira',
  inteligencia: 'mesa',
  editorias: 'editoria',
  horoscopo: 'motor',
  ion_hub: 'TV',
};

function tempoDesde(iso?: string | null): string {
  if (!iso) return '';
  const min = Math.round((Date.now() - new Date(iso).getTime()) / 60_000);
  if (!Number.isFinite(min) || min < 0) return '';
  if (min < 60) return `há ${min} min`;
  const h = Math.round(min / 60);
  return h < 48 ? `há ${h}h` : `há ${Math.round(h / 24)}d`;
}

function TrendCard({
  t,
  geo,
  estado,
  marca,
  onOpen,
  onProduced,
}: {
  t: TrendItem;
  geo: string;
  estado?: import('@/services/board').EstadoConteudo;
  /** Marca assumida pela sessao STAFF; undefined em tenant (la o client_id resolve). */
  marca?: string;
  onOpen: () => void;
  onProduced?: (msg: string) => void;
}) {
  const ago = timeAgo(t.start_timestamp);
  const published = Boolean(estado?.publicado);
  const origem = estado?.fonte ? (ORIGEM_LABEL[estado.fonte] ?? estado.fonte) : '';
  const quando = tempoDesde(estado?.quando);
  // Handoff leve direto do card (sem abrir o modal): manda o tema pra Produção
  // pra outra pessoa ver ângulo/editar. `intel` é opcional no enqueue.
  const [erro, setErro] = useState<string | null>(null);
  const produzir = useMutation({
    mutationFn: () => boardApi.enqueue({ tema: t.query, fonte: 'inteligencia', site: marca }),
    onSuccess: () => {
      setErro(null);
      // Sempre nasce card novo: desde 07/08 o quadro não recusa mais o segundo
      // card do mesmo assunto (ele tem hora, autor e ID próprios). Cards do
      // mesmo assunto aparecem agrupados por cor no quadro.
      onProduced?.(`"${t.query}" enviado para Produção.`);
      useNavPulse.getState().pulseProducao();
    },
    // A recusa mais provavel aqui e o 422 de marca ausente, que por desenho nao
    // levanta alarme global: sem este onError a falha nao aparecia em lugar
    // nenhum e o botao parecia quebrado.
    onError: (e) => setErro(motivoDoEnvio(e)),
  });
  return (
    <div className={styles.card}>
      <button type="button" className={styles.cardBody} onClick={onOpen}>
      <div className={styles.cardTop}>
        <span className={styles.edBadge}>{t.editoria ?? 'Geral'}</span>
        {published && (
          <span
            className={styles.pubBadge}
            title={estado?.tema_publicado ? `"${estado.tema_publicado}"` : undefined}
          >
            publicado{origem ? ` · ${origem}` : ''}{quando ? ` · ${quando}` : ''}
          </span>
        )}
        {/* "declínio" era palavra nossa: o dado é um booleano e ninguém mediu
            queda nenhuma. E o Google mostra o início de TODAS, não só das vivas. */}
        <span className={t.active ? styles.dirUp : styles.dirDown}>
          {t.active ? '▲ em alta' : 'Encerrada'}
          {ago ? ` · começou ${ago}` : ''}
        </span>
      </div>

      <h3 className={styles.cardTitle}>{t.query}</h3>

      <div className={styles.cardMetrics}>
        {/* Nota e banda são JULGAMENTO NOSSO. Num assunto que o Google já
            encerrou, "Ótimo tema" e um número grande são a persuasão que induz
            ao erro. O botão continua: quem desligou "só ativas" fez uma escolha. */}
        {t.active && (
          <div className={styles.metric}>
            <span className={styles.metricVal} style={{ color: scoreColor(t.score) }}>{t.score}</span>
            <span className={styles.metricLbl}>score</span>
          </div>
        )}
        <div className={styles.metric}>
          <span className={styles.metricVal}>{volumeDeBusca(t.search_volume)}</span>
          <span className={styles.metricLbl}>buscas</span>
        </div>
        <div className={styles.metric}>
          <span className={styles.metricVal}>{t.increase_percentage ? `+${t.increase_percentage}%` : '—'}</span>
          <span className={styles.metricLbl}>variação</span>
        </div>
      </div>

      {/* mini-gráfico: decomposição do score (sem nova chamada) */}
      <div className={styles.bars}>
        <Bar label="audiência" v={t.audiencia} />
        <Bar label="momentum" v={t.momentum} />
        <Bar label="fit" v={t.fit} />
      </div>

      <div className={styles.cardFoot}>
        {t.active && (
          <span className={`${styles.goodTag} ${styles[`band-${t.band}`]}`}>{bandLabel(t.band)}</span>
        )}
        {/* O selo "potencial furo" morava aqui e era calculado SEM contar
            veículos. Em 06/08 ele apareceu em "corinthians x internacional",
            1 milhão de buscas — o assunto mais coberto do país. Furo exige 3
            veículos ou menos, e essa contagem só existe no detalhe. */}
        {t.flags.map((f) => (
          <span key={f} className={styles.flagTag}>{flagLabel(f)}</span>
        ))}
        <span className={styles.geoTag}>{geoLabel(geo)}</span>
      </div>

      {t.trend_breakdown.length > 0 && (
        <div className={styles.cardChips}>
          {t.trend_breakdown.slice(0, 3).map((b) => (
            <span key={b} className={styles.chip}>{b}</span>
          ))}
          {/* O Google escreve "E mais 14". Cortar em três e calar os outros
              faz a tendência parecer mais magra do que é. */}
          {t.trend_breakdown.length > 3 && (
            <span className={styles.chip}>+{t.trend_breakdown.length - 3}</span>
          )}
        </div>
      )}
      </button>
      <button
        type="button"
        className={styles.produzirBtn}
        disabled={produzir.isPending}
        onClick={() => produzir.mutate()}
      >
        {produzir.isPending ? 'Enviando…' : 'Produzir →'}
      </button>
      {erro && <p className={styles.error}>{erro}</p>}
    </div>
  );
}

function Bar({ label, v }: { label: string; v: number }) {
  return (
    <div className={styles.barRow}>
      <span className={styles.barLbl}>{label}</span>
      <span className={styles.barTrack}>
        <span className={styles.barFill} style={{ width: `${v}%` }} />
      </span>
      <span className={styles.barNum}>{v}</span>
    </div>
  );
}

// ─── Modal denso ─────────────────────────────────────────────────────────────

function TopicModal({
  item,
  marca,
  onClose,
  onFeedback,
}: {
  item: TrendItem;
  /** Marca assumida pela sessao STAFF; undefined em tenant (la o client_id resolve). */
  marca?: string;
  onClose: () => void;
  onFeedback: (m: string) => void;
}) {
  const [editoria, setEditoria] = useState(item.editoria ?? '');
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [frammingOpen, setFrammingOpen] = useState(false);
  const [destino, setDestino] = useState(''); // E5-01: destino opcional já na curadoria
  const [erroEnvio, setErroEnvio] = useState<string | null>(null);
  const sitesQuery = useQuery({
    queryKey: ['wp-sites', 'active'],
    queryFn: () => destinationsApi.list(),
    staleTime: 300_000,
  });

  const topicQuery = useQuery({
    queryKey: ['intel', 'topic', item.query],
    queryFn: () =>
      intelligenceService.getTopic({
        query: item.query,
        search_volume: item.search_volume,
        increase_percentage: item.increase_percentage,
        editoria: item.editoria,
        // a nota do card viaja junto — o detalhe repete, não fabrica
        score: item.score,
        confianca: item.confianca,
        flags: item.flags,
        active: item.active,
      }),
  });

  useEffect(() => {
    if (topicQuery.data && !editoria && topicQuery.data.editoria) {
      setEditoria(topicQuery.data.editoria);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topicQuery.data]);

  const detail = topicQuery.data;

  const enviarQuadro = useMutation({
    mutationFn: () =>
      boardApi.enqueue({
        tema: item.query,
        fonte: 'inteligencia',
        // E5-01: destino ja viaja (opcional). Vazio numa sessao da casa cai na
        // marca assumida no topo — sem ela o backend devolve 422 e o card nao nasce.
        site: destino || marca || undefined,
        editoria: editoria || detail?.editoria || undefined,
        // B-13: handoff por referência (snapshot persistido). Fallback inline se ainda
        // não houver intel_id (detail recém-carregado).
        intel_id: detail?.intel_id ?? undefined,
        intel: detail && !detail.intel_id ? { ...detail, editoria: editoria || detail.editoria } : undefined,
      }),
    onSuccess: () => {
      setErroEnvio(null);
      onFeedback(`"${item.query}" enviado para o quadro de Produção.`);
      useNavPulse.getState().pulseProducao(); // pisca a aba Produção
      onClose();
    },
    // O modal fica ABERTO na falha de proposito: o seletor de destino esta a
    // dois centimetros da mensagem, entao quem leu conserta no mesmo gesto.
    onError: (e) => setErroEnvio(motivoDoEnvio(e)),
  });

  // Texto humano do motivo (teste 03/09: "0 veículos" virava "furo" mesmo sem chave).
  const motivoCobertura = (r?: string | null) => {
    if (r === 'serp_key_missing') return 'chave SERP não configurada (Config → APIs externas)';
    if (r === 'serp_quota') return 'cota mensal de buscas esgotada';
    // 05/09/2026: o Google News da SerpApi pendurava ~90 s e voltava 503. O
    // resto do card (score, editoria, perguntas) veio normalmente.
    if (r === 'serp_timeout') return 'o provedor de busca demorou demais para responder';
    if (r === 'serp_provider') return 'o provedor de busca falhou';
    if (r?.startsWith('serp_error:')) return `erro do provedor: ${r.slice('serp_error:'.length)}`;
    return 'provedor de busca sem resposta';
  };

  const grouped = useMemo(() => {
    const map = new Map<string, NewsCard[]>();
    for (const n of detail?.news ?? []) {
      const k = n.source ?? 'Outros';
      if (!map.has(k)) map.set(k, []);
      map.get(k)!.push(n);
    }
    return [...map.entries()].sort((a, b) => b[1].length - a[1].length);
  }, [detail?.news]);

  const addTag = () => {
    const v = tagInput.trim();
    if (v && !tags.includes(v)) setTags([...tags, v]);
    setTagInput('');
  };

  return (
    <div className={styles.modalBackdrop} onClick={onClose}>
      <aside className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <header className={styles.modalHead}>
          <div>
            <h2 className={styles.modalTitle}>{item.query}</h2>
            <span className={styles.modalSub}>
              {/* mesma palavra do card: "declínio" era invenção nossa */}
              {item.active ? '▲ em alta' : 'Encerrada'} ·{' '}
              {volumeDeBusca(item.search_volume)} buscas ·{' '}
              {item.increase_percentage ? `+${item.increase_percentage}%` : 'sem variação'}
            </span>
          </div>
          <button className={styles.modalClose} onClick={onClose}>fechar</button>
        </header>

        {topicQuery.isLoading && <p className={styles.muted}>A analisar cobertura e perguntas…</p>}
        {topicQuery.isError && (
          <p className={styles.error}>
            {(topicQuery.error as { status?: number } | null)?.status === 503
              ? // Fornecedor fora ≠ defeito nosso. Dizer "falha ao detalhar"
                // mandava o operador tentar de novo no mesmo segundo, contra o
                // mesmo provedor pendurado (incidente 05/09/2026).
                'O provedor de busca não respondeu. O quadro de tendências continua válido — tente este card de novo em alguns minutos.'
              : 'Falha ao detalhar o tópico.'}
          </p>
        )}

        {detail && (
          <div className={styles.modalBody}>
            {/* coluna esquerda: contexto + cobertura */}
            <div className={styles.colMain}>
              <div className={styles.verdictRow}>
                <span className={`${styles.verdict} ${verdictTone(detail.verdict)}`}>
                  {detail.verdict}
                </span>
                <span className={styles.verdictMotivo}>{detail.motivo}</span>
              </div>

              <div className={styles.section}>
                <span className={styles.sectionLabel}>
                  Cobertura por veículo · {grouped.length} veículos · {detail.news.length} matérias
                </span>
                {grouped.length > 0 ? (
                  <div className={styles.groups}>
                    {grouped.map(([source, arts]) => (
                      <div key={source} className={styles.group}>
                        <div className={styles.groupHead}>
                          {arts[0]?.source_icon && (
                            <img className={styles.newsIcon} src={arts[0].source_icon} alt="" />
                          )}
                          <span className={styles.groupName}>{source}</span>
                          <span className={styles.groupCount}>{arts.length}</span>
                        </div>
                        <ul className={styles.groupList}>
                          {arts.map((n, i) => (
                            <li key={n.link ?? `${n.title}-${i}`} className={styles.groupItem}>
                              {n.link ? (
                                <a href={n.link} target="_blank" rel="noopener noreferrer">
                                  {n.title}
                                </a>
                              ) : (
                                <span>{n.title}</span>
                              )}
                              {n.date && <span className={styles.newsDate}> · {formatNewsDate(n.date)}</span>}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                ) : detail.coverage_status === 'unavailable' ? (
                  <p className={styles.error}>
                    Cobertura indisponível — {motivoCobertura(detail.coverage_reason)}.
                    Não é sinal de furo: o provedor de busca não respondeu.
                  </p>
                ) : (
                  detail?.cobertura_indisponivel ? (
                    // Busca falhou ≠ ninguém cobre. Afirmar "furo" aqui induziu a
                    // redação ao erro em 03/09 (assunto com 100 notícias no Google).
                    <p className={styles.error}>
                      {detail.coverage_reason === 'serp_no_results'
                        ? 'O Google Notícias não trouxe resultado para esta frase exata — costuma ser a redação da tendência, não falta de cobertura.'
                        : 'Não foi possível medir a cobertura agora (provedor de busca sem resposta).'}
                      {' '}Confira no Google Notícias antes de tratar como furo.
                    </p>
                  ) : (
                    <p className={styles.muted}>Sem cobertura detetada — forte sinal de furo.</p>
                  )
                )}
              </div>

              <div className={styles.section}>
                <span className={styles.sectionLabel}>People Also Ask</span>
                <p className={styles.paaPitch}>
                  Estas perguntas são as dúvidas do público e podem gerar engajamento
                  em todos os nossos veículos.
                </p>
                {detail.paa.length > 0 ? (
                  <ul className={styles.paa}>
                    {detail.paa.map((q) => (
                      <li key={q}>{q}</li>
                    ))}
                  </ul>
                ) : (
                  <p className={styles.muted}>Sem PAA para este termo (típico de nomes próprios).</p>
                )}
              </div>

              {detail.keywords.length > 0 && (
                <div className={styles.section}>
                  <span className={styles.sectionLabel}>
                    Palavras-chave — buscas reais do público (SEO)
                  </span>
                  <p className={styles.muted}>
                    Vão direto para o escritor de matérias. Clique para também usar como tag.
                  </p>
                  <div className={styles.cardChips}>
                    {detail.keywords.map((k) => (
                      <button
                        key={k}
                        type="button"
                        className={styles.suggestChip}
                        onClick={() => !tags.includes(k) && setTags([...tags, k])}
                      >
                        + {k}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* coluna direita: edição + ações */}
            <aside className={styles.colSide}>
              <div className={styles.sideCard}>
                <span className={styles.sideLabel}>Editoria</span>
                <select
                  className={styles.sideSelect}
                  value={editoria}
                  onChange={(e) => setEditoria(e.target.value)}
                >
                  <option value="">— escolher —</option>
                  {EDITORIAS.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>

              <div className={styles.sideCard}>
                <span className={styles.sideLabel}>Tags</span>
                <div className={styles.tagRow}>
                  <input
                    className={styles.tagInput}
                    value={tagInput}
                    placeholder="adicionar tag…"
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') { e.preventDefault(); addTag(); }
                    }}
                  />
                  <Button size="sm" variant="secondary" onClick={addTag}>+</Button>
                </div>
                {tags.length > 0 && (
                  <div className={styles.tagChips}>
                    {tags.map((tg) => (
                      <span key={tg} className={styles.tagChip} onClick={() => setTags(tags.filter((x) => x !== tg))}>
                        {tg} ×
                      </span>
                    ))}
                  </div>
                )}
                {item.trend_breakdown.length > 0 && (
                  <div className={styles.tagSuggest}>
                    <span className={styles.muted}>sugestões:</span>
                    {item.trend_breakdown.map((b) => (
                      <button
                        key={b}
                        type="button"
                        className={styles.suggestChip}
                        onClick={() => !tags.includes(b) && setTags([...tags, b])}
                      >
                        + {b}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className={styles.sideActions}>
                <Button onClick={() => setFrammingOpen(true)}>
                  Gerar só este assunto (com ângulo)
                </Button>
                <select
                  value={destino}
                  onChange={(e) => setDestino(e.target.value)}
                  style={{ fontSize: 13, padding: '4px 8px' }}
                  title="Destino opcional — quem produzir pode escolher depois"
                >
                  <option value="">destino (o produtor escolhe)</option>
                  {(sitesQuery.data ?? []).map((s) => (
                    <option key={s.slug} value={s.slug}>{s.name}</option>
                  ))}
                </select>
                <Button
                  variant="secondary"
                  disabled={enviarQuadro.isPending}
                  onClick={() => enviarQuadro.mutate()}
                >
                  Enviar para o quadro de Produção
                </Button>
              </div>
              {erroEnvio && <p className={styles.error}>{erroEnvio}</p>}
              <p className={styles.hint}>
                O conteúdo é redigido a partir do PAA + cobertura + dados da tendência,
                com prompt travado. Você escolhe o destino e o ângulo; sai como rascunho
                no WordPress.
              </p>
            </aside>
          </div>
        )}
      </aside>

      <FrammingModal
        open={frammingOpen}
        onClose={() => setFrammingOpen(false)}
        sourceType="trends"
        tema={item.query}
        marcaFallback={marca}
        payload={{ query: item.query, editoria: editoria || item.editoria || undefined, tags }}
        intel={detail ? { ...detail, editoria: editoria || detail.editoria } : undefined}
        sourceOptions={detail?.news}
        onGenerated={(r) =>
          onFeedback(
            r.wp_link
              ? `Rascunho gerado com ângulo: "${r.title}" (#${r.wp_id}).`
              : `Matéria gerada: "${r.title}".`,
          )
        }
        onProduced={() => {
          onFeedback(`"${item.query}" enviado para Produção — produzindo com ângulo.`);
          useNavPulse.getState().pulseProducao();
        }}
      />
    </div>
  );
}
